<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ScheduledMessagesAPI from 'dashboard/api/scheduledMessages';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
  messageContent: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:show', 'close', 'scheduled']);

const { t } = useI18n();

const content = ref('');
const scheduledAt = ref('');
const recurrenceType = ref('');
const recurrenceEndAt = ref('');
const isSubmitting = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const minDateTime = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return now.toISOString().slice(0, 16);
});

const recurrenceOptions = computed(() => [
  {
    value: '',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_NONE'),
  },
  {
    value: 'daily',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_DAILY'),
  },
  {
    value: 'weekly',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_WEEKLY'),
  },
  {
    value: 'monthly',
    label: t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_MONTHLY'),
  },
]);

const isFormValid = computed(() => {
  return content.value.trim() && scheduledAt.value;
});

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      content.value = props.messageContent || '';
      scheduledAt.value = '';
      recurrenceType.value = '';
      recurrenceEndAt.value = '';
    }
  }
);

const onClose = () => {
  emit('close');
  emit('update:show', false);
};

const onSchedule = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    const data = {
      content: content.value,
      scheduled_at: new Date(scheduledAt.value).toISOString(),
    };

    if (recurrenceType.value) {
      data.recurrence_type = recurrenceType.value;
      if (recurrenceEndAt.value) {
        data.recurrence_end_at = new Date(recurrenceEndAt.value).toISOString();
      }
    }

    await ScheduledMessagesAPI.createForConversation(
      props.conversationId,
      data
    );
    useAlert(t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.SUCCESS'));
    emit('scheduled');
    onClose();
  } catch (error) {
    useAlert(t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <woot-modal-header
      :header-title="t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.MODAL_TITLE')"
      :header-content="
        t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.MODAL_DESCRIPTION')
      "
    />
    <div class="flex flex-col gap-5 p-6">
      <!-- Message Field -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="content"
          class="w-full min-h-[120px] px-3 py-3 text-sm text-n-slate-12 placeholder-n-slate-10 bg-n-alpha-black3 border border-n-strong rounded-lg resize-none transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
          :placeholder="
            t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.MESSAGE_PLACEHOLDER')
          "
        />
      </div>

      <!-- Schedule Date/Time Field -->
      <div class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-calendar-clock text-n-slate-11" />
          {{ t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.SCHEDULED_AT_LABEL') }}
        </label>
        <input
          v-model="scheduledAt"
          type="datetime-local"
          :min="minDateTime"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        />
      </div>

      <!-- Recurrence Field -->
      <div class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-repeat text-n-slate-11" />
          {{ t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_LABEL') }}
        </label>
        <select
          v-model="recurrenceType"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent cursor-pointer"
        >
          <option
            v-for="option in recurrenceOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Recurrence End Date (conditional) -->
      <div v-if="recurrenceType" class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-calendar-x text-n-slate-11" />
          {{ t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.RECURRENCE_END_LABEL') }}
        </label>
        <input
          v-model="recurrenceEndAt"
          type="date"
          :min="scheduledAt ? scheduledAt.split('T')[0] : ''"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 pt-4 mt-2 border-t border-n-strong">
        <NextButton
          :label="t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.CANCEL_BUTTON')"
          slate
          faded
          md
          @click="onClose"
        />
        <NextButton
          :label="t('CONVERSATION.REPLYBOX.SCHEDULE_MESSAGE.SCHEDULE_BUTTON')"
          icon="i-lucide-clock"
          blue
          solid
          md
          :disabled="!isFormValid || isSubmitting"
          :is-loading="isSubmitting"
          @click="onSchedule"
        />
      </div>
    </div>
  </woot-modal>
</template>
